import { DateTime } from "luxon";
import { makeDateString, type DateString } from "../api/types";

export const PERIOD_IDS = [
  "today",
  "yesterday",
  "thisWeek",
  "lastWeek",
  "last7Days",
  "thisMonth",
  "lastMonth",
  "last30Days",
  "ytd",
  "thisYear",
  "lastYear",
] as const;

export type PeriodId = (typeof PERIOD_IDS)[number];

export const PERIOD_LABELS: Record<PeriodId, string> = {
  today: "Today",
  yesterday: "Yesterday",
  thisWeek: "This week",
  lastWeek: "Last week",
  last7Days: "Last 7 days",
  thisMonth: "This month",
  lastMonth: "Last month",
  last30Days: "Last 30 days",
  ytd: "Year to date",
  thisYear: "This year",
  lastYear: "Last year",
};

export const isPeriodId = (s: string | null | undefined): s is PeriodId =>
  s != null && (PERIOD_IDS as readonly string[]).includes(s);

const iso = (dt: DateTime): DateString => makeDateString(dt.toISODate() ?? "");

export type ResolvedRange = {
  startDate: DateString;
  endDate: DateString;
};

export const resolvePeriod = (
  id: PeriodId,
  now: DateTime = DateTime.now(),
): ResolvedRange => {
  switch (id) {
    case "today":
      return { startDate: iso(now), endDate: iso(now) };
    case "yesterday": {
      const d = now.minus({ days: 1 });
      return { startDate: iso(d), endDate: iso(d) };
    }
    case "thisWeek":
      return {
        startDate: iso(now.startOf("week")),
        endDate: iso(now.endOf("week")),
      };
    case "lastWeek": {
      const w = now.minus({ weeks: 1 });
      return {
        startDate: iso(w.startOf("week")),
        endDate: iso(w.endOf("week")),
      };
    }
    case "last7Days":
      return { startDate: iso(now.minus({ days: 6 })), endDate: iso(now) };
    case "thisMonth":
      return {
        startDate: iso(now.startOf("month")),
        endDate: iso(now.endOf("month")),
      };
    case "lastMonth": {
      const m = now.minus({ months: 1 });
      return {
        startDate: iso(m.startOf("month")),
        endDate: iso(m.endOf("month")),
      };
    }
    case "last30Days":
      return { startDate: iso(now.minus({ days: 29 })), endDate: iso(now) };
    case "ytd":
      return { startDate: iso(now.startOf("year")), endDate: iso(now) };
    case "thisYear":
      return {
        startDate: iso(now.startOf("year")),
        endDate: iso(now.endOf("year")),
      };
    case "lastYear": {
      const y = now.minus({ years: 1 });
      return {
        startDate: iso(y.startOf("year")),
        endDate: iso(y.endOf("year")),
      };
    }
  }
};
